import { SeamTapeGrain } from '@seddi/scene';
import {
  type Material,
  type MeshPhysicalMaterial,
  type Object3D,
  type Texture,
  BackSide,
  BufferAttribute,
  BufferGeometry,
  Matrix3,
  Mesh,
  MeshStandardMaterial,
} from '@seddi/three';

import { assert } from '../core/assert';
import type { GarmentGeometryGfx } from '../resources/garmentGeometryTypes';
import { clone as cloneMaterial } from '../resources/material';
import { ResourcesContext } from '../resources/resourcesContext';
import { textureMapsKeys } from '../resources/textureTypes';

export type SeamTapeRenderable = {
  readonly id: string;
  fabricRef?: string;
  garmentGeometryRef?: string;
  readonly mesh: Mesh;
  readonly parent: Object3D;
};

/**
 * Create seam tape renderable.
 * @param id - Seam ID.
 * @param parent - Seam object.
 */
export const create = (id: string, parent: Object3D): SeamTapeRenderable => {
  // Create new mesh object with placeholder geometry and materials.
  const materials = [materialPlaceholder1, materialPlaceholder0];
  const mesh = new Mesh(geometryPlaceholder, materials);
  mesh.name = `${id}-tape`;
  // Hierarchy remains immutable.
  parent.add(mesh);
  // Return new instance.
  return { id, mesh, parent };
};

/**
 * Dispose seam tape renderable.
 * @param self - Seam tape renderable.
 * @param resources - Resources manager.
 */
export const dispose = (self: SeamTapeRenderable, resources: ResourcesContext) => {
  // Detach fabric.
  detachFabric(self, resources);
  // Detach garment geometry.
  detachGarmentGeometry(self, resources);
  // Remove from parent.
  self.parent.remove(self.mesh);
};

/**
 * Attach fabric.
 * @param self - Seam tape renderable.
 * @param id - Fabric ID.
 * @param resources - Resources context;
 */
export const attachFabric = (
  self: SeamTapeRenderable,
  id: string,
  grain: SeamTapeGrain,
  resources: ResourcesContext,
) => {
  if (self.fabricRef) {
    detachFabric(self, resources);
  }

  // Create material instances.
  const fabric = resources.getRefFabric(id);
  const material0 = cloneMaterial(fabric.back || fabric.front);
  const material1 = cloneMaterial(fabric.front);
  material0.side = BackSide;
  const materials = self.mesh.material as unknown as Material[];
  materials[0] = material0;
  materials[1] = material1;
  self.fabricRef = id;

  updateGrain(self, grain);
};

/**
 * Detach fabric.
 * @param self - Seam tape renderable.
 * @param resources - Resources context;
 */
export const detachFabric = (self: SeamTapeRenderable, resources: ResourcesContext) => {
  if (!self.fabricRef) {
    return;
  }
  const mesh = self.mesh;
  const materials = mesh.material as unknown as Material[];
  materials[0].dispose();
  materials[0] = materialPlaceholder0;
  materials[1].dispose();
  materials[1] = materialPlaceholder1;
  resources.retRefFabric(self.fabricRef);
  delete self.fabricRef;
};

/**
 * Attach garment geometry.
 * @param self - Seam tape renderable.
 * @param garmentGeometry - Garment geometry.
 * @param resources - Resources context.
 */
export const attachGarmentGeometry = (
  self: SeamTapeRenderable,
  garmentGeometry: GarmentGeometryGfx,
  resources: ResourcesContext,
) => {
  if (self.garmentGeometryRef) {
    detachGarmentGeometry(self, resources);
  }
  // const garmentGeometry = resources.getRefGarmentGeometry(garmentGeometryId);
  // self.garmentGeometryRef = garmentGeometryId;
  const geometry = garmentGeometry.virtualPieces.get(self.id)!;
  assert(geometry, `Virtual piece ${self.id} not found`);
  self.mesh.geometry = geometry;
  // Recreate sub-meshes.
  recreateGroups(self);
};

/**
 * Detach garment geometry.
 * @param self - Seam tape renderable.
 * @param resources - Resources context;
 */
export const detachGarmentGeometry = (self: SeamTapeRenderable, resources: ResourcesContext) => {
  // if (!self.garmentGeometryRef) {
  //   return;
  // }
  self.mesh.geometry = geometryPlaceholder;
  // Recreate sub-meshes.
  recreateGroups(self);
  // resources.retRefGarmentGeometry(self.garmentGeometryRef);
  // delete self.garmentGeometryRef;
};

/**
 * Update grain.
 * @param self - Seam tape renderable.
 * @param grain - Grain option.
 */
export const updateGrain = (() => {
  const M = new Matrix3();
  const R_1 = new Matrix3();

  return (self: SeamTapeRenderable, grain: SeamTapeGrain) => {
    let rotation = 0;
    switch (grain) {
      case SeamTapeGrain.BIAS:
        rotation = Math.PI / 4;
        break;
      case SeamTapeGrain.CROSS:
        rotation = Math.PI / 2;
        break;
    }

    const mesh = self.mesh;
    const materials = Array.isArray(mesh.material)
      ? (mesh.material as MeshPhysicalMaterial[])
      : [mesh.material as MeshPhysicalMaterial];

    // Apply inverse grain rotation.
    R_1.makeRotation(-rotation);

    // Update every texture key found on each material.
    for (const material of materials) {
      if (!material.userData?.fabric) {
        continue;
      }

      // M = S x R-1
      const { repeat } = material.map;
      // For back-side textures apply horizontal flip.
      const sx = material.side === BackSide ? -1 : 1;
      M.makeScale(sx * repeat.x, repeat.y);
      M.multiply(R_1);

      for (const key of textureMapsKeys) {
        const texture = material[key] as Texture;
        if (!texture) {
          continue;
        }

        texture.matrix.copy(M);
        texture.matrixAutoUpdate = false;
      }
    }
  };
})();

/**
 * Recreate geometry groups.
 * @param self - Seam tape renderable.
 */
const recreateGroups = (self: SeamTapeRenderable) => {
  const mesh = self.mesh;
  const geometry = mesh.geometry;
  // Group geometry several times with different materials.
  const start = 0;
  const count = geometry.index?.count ?? 0;
  geometry.clearGroups();
  for (let i = 0; i < 2; ++i) {
    geometry.addGroup(start, count, i);
  }
};

// ------ PLACEHOLDERS ------

// Placeholders are defined in global context for now.
const geometryPlaceholder = new BufferGeometry();
const materialPlaceholder0 = new MeshStandardMaterial({ color: '#C4C4C4' });
const materialPlaceholder1 = new MeshStandardMaterial({ color: '#C4C4C4' });
materialPlaceholder1.side = BackSide;
